import { BackSide, type Color, Mesh, ShaderMaterial, SphereGeometry, Vector3 } from 'three';
import { look } from './look';
import { mix, PAL } from './palette';

/**
 * Sky in flat bands (poster style) with a big pale flat sun and a soft ring.
 * Follows the camera; drawn first, never fogged.
 */
export const SKY = {
  // Bands from the horizon up.
  horizon: mix('ochre', 'cream', 0.24),
  low: mix('ochre', 'cream', 0.06),
  mid: PAL.ochre,
  high: mix('ochre', 'terracotta', 0.2),
  top: mix('ochre', 'terracotta', 0.34),
  sun: PAL.cream,
  halo: mix('ochre', 'cream', 0.55),
};

/** The sky of the setting being built (look.ts; Tuscany's is SKY's). */
export function makeSky(sunDir: Vector3, sky = look().sky): Mesh {
  const col = (c: Color): Vector3 => new Vector3(c.r, c.g, c.b);
  const mat = new ShaderMaterial({
    side: BackSide,
    depthWrite: false,
    depthTest: false,
    fog: false,
    uniforms: {
      uSun: { value: sunDir.clone().normalize() },
      uHorizon: { value: col(sky.horizon) },
      uLow: { value: col(sky.low) },
      uMid: { value: col(sky.mid) },
      uHigh: { value: col(sky.high) },
      uTop: { value: col(sky.top) },
      uSunCol: { value: col(sky.sun) },
      uHalo: { value: col(sky.halo) },
      // Screen-space sun (pixels) so it stays a perfect flat disc, poster-style.
      uSunPx: { value: new Vector3(-1e4, -1e4, 0) },
      uSunR: { value: 80 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        gl_Position = p.xyww;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uSun, uHorizon, uLow, uMid, uHigh, uTop, uSunCol, uHalo, uSunPx;
      uniform float uSunR;
      varying vec3 vDir;
      float band(float e, float at, float soft) { return smoothstep(at - soft, at + soft, e); }
      void main() {
        vec3 d = normalize(vDir);
        float e = degrees(asin(clamp(d.y, -1.0, 1.0)));
        vec3 c = uHorizon;
        c = mix(c, uLow, band(e, 2.0, 0.15));
        c = mix(c, uMid, band(e, 6.0, 0.2));
        c = mix(c, uHigh, band(e, 13.5, 0.6));
        c = mix(c, uTop, band(e, 24.0, 2.5));
        // Faint horizontal brush streaks.
        float streak = sin(e * 3.1 + sin(d.x * 3.0 + d.z * 2.0) * 1.3) * 0.5 + 0.5;
        c *= 1.0 + (streak - 0.5) * 0.018;
        // Big pale flat sun with a flat halo ring, drawn as a true circle on screen.
        if (uSunPx.z > 0.5) {
          float r = distance(gl_FragCoord.xy, uSunPx.xy);
          c = mix(c, uHalo, (1.0 - smoothstep(uSunR * 1.15, uSunR * 1.9, r)) * 0.22);
          c = mix(c, uSunCol, 1.0 - smoothstep(uSunR, uSunR + 1.5, r));
        }
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }
    `,
  });
  const m = new Mesh(new SphereGeometry(5000, 32, 16), mat);
  m.renderOrder = -10;
  m.frustumCulled = false;
  return m;
}
